/**
 * Authentication Hooks using React Query
 * 
 * Provides persistent, background-synchronized authentication state
 * with automatic token management and validation.
 */

import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authService } from '../services/auth'
import { api } from '../services/api'
import type { User, LoginRequest, RegisterRequest, AuthResponse } from '../types'
import { logger } from '../utils/logger'

// Query Keys
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  token: () => [...authKeys.all, 'token'] as const,
}

/**
 * Get stored token from localStorage
 */
const getStoredToken = (): string | null => {
  try {
    // Try to get from bgai-auth-storage first (primary storage)
    const storedState = localStorage.getItem('bgai-auth-storage')
    if (storedState) {
      const parsed = JSON.parse(storedState)
      const token = parsed.state?.token
      if (token) {
        return token
      }
    }

    // Fallback: Try standalone token storage (for backward compatibility)
    const standaloneToken = localStorage.getItem('token')
    if (standaloneToken) {
      logger.debug('Using standalone token from localStorage', {
        component: 'useAuth',
        hasToken: true
      })
      return standaloneToken
    }
  } catch (error) {
    logger.warn('Failed to get stored token', {
      component: 'useAuth',
      error: error instanceof Error ? error.message : 'Unknown error'
    })
  }
  return null
}

/**
 * Store authentication data in localStorage
 */
const storeAuthData = (authResponse: AuthResponse) => {
  try {
    const authData = {
      state: {
        user: authResponse.user,
        token: authResponse.access_token,
        isAuthenticated: true
      }
    }
    
    localStorage.setItem('bgai-auth-storage', JSON.stringify(authData))
    
    // Store user type separately for easy access by auth service
    const userType = authResponse.user?.role || authResponse.user?.user_type || 'company_owner'
    localStorage.setItem('user_type', userType)
    
    // Set token in axios headers immediately
    api.defaults.headers.common['Authorization'] = `Bearer ${authResponse.access_token}`
    
    logger.debug('Auth data stored successfully', {
      component: 'useAuth',
      userId: authResponse.user?.id,
      userType: userType
    })
    
  } catch (error) {
    logger.error('Failed to store auth data', {
      component: 'useAuth',
      error: error instanceof Error ? error.message : 'Unknown error'
    })
  }
}

/**
 * Clear authentication data
 */
const clearAuthData = () => {
  try {
    localStorage.removeItem('bgai-auth-storage')
    localStorage.removeItem('user_type')
    delete api.defaults.headers.common['Authorization']
    
    logger.debug('Auth data cleared', { component: 'useAuth' })
  } catch (error) {
    logger.error('Failed to clear auth data', {
      component: 'useAuth',
      error: error instanceof Error ? error.message : 'Unknown error'
    })
  }
}

/**
 * Hook for current user data
 * Automatically validates token and fetches user info
 */
export const useUser = () => {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async (): Promise<User | null> => {
      const currentToken = getStoredToken()
      if (!currentToken) {
        return null
      }
      
      // Set token in headers before making request
      api.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`
      
      try {
        const user = await authService.getCurrentUser()
        
        // Ensure WebSocket service has the current token
        const { websocketService } = await import('../services/websocket')
        websocketService.setAuthToken(currentToken)
        
        return user
      } catch (error) {
        // Clear invalid token
        clearAuthData()
        return null
      }
    },
    enabled: !!getStoredToken(), // Only run if we have a token
    staleTime: Infinity, // Never consider stale to prevent refetching
    gcTime: Infinity, // Keep in cache forever
    retry: false, // Don't retry auth failures
    refetchOnMount: false, // Don't refetch on mount
    refetchOnReconnect: false, // Don't refetch on reconnect
    refetchOnWindowFocus: false, // Don't refetch on focus
  })
}

/**
 * Login mutation
 */
export const useLogin = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (credentials: LoginRequest): Promise<AuthResponse> => {
      logger.debug('Attempting login', {
        component: 'useLogin',
        email: credentials.email
      })
      
      const response = await authService.login(credentials)
      
      // Store auth data immediately
      storeAuthData(response)
      
      return response
    },
    
    onSuccess: async (data) => {
      logger.debug('Login successful', {
        component: 'useLogin',
        userId: data.user?.id
      })
      
      // Reset stores for clean user session
      const { resetAllStores } = await import('../stores/storeManager')
      resetAllStores()
      
      // Update WebSocket service with new auth token
      const { websocketService } = await import('../services/websocket')
      websocketService.setAuthToken(data.access_token)
      
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: authKeys.user() })
      
      // Set user data in cache immediately
      queryClient.setQueryData(authKeys.user(), data.user)
    },
    
    onError: (error: any) => {
      logger.error('Login failed', {
        component: 'useLogin',
        error: error.message
      })
      
      // Clear any stored auth data on login failure
      clearAuthData()
    },
  })
}

/**
 * Register mutation
 */
export const useRegister = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: RegisterRequest): Promise<AuthResponse> => {
      logger.debug('🔥 useRegister mutationFn called', {
        component: 'useRegister',
        email: data.email,
        timestamp: new Date().toISOString()
      })
      
      const response = await authService.register(data)
      
      // Store auth data immediately
      storeAuthData(response)
      
      return response
    },
    
    onSuccess: async (data) => {
      logger.debug('Registration successful', {
        component: 'useRegister',
        userId: data.user?.id
      })
      
      // Reset stores for clean user session
      const { resetAllStores } = await import('../stores/storeManager')
      resetAllStores()
      
      // Update WebSocket service with new auth token
      const { websocketService } = await import('../services/websocket')
      websocketService.setAuthToken(data.access_token)
      
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: authKeys.user() })
      
      // Set user data in cache immediately
      queryClient.setQueryData(authKeys.user(), data.user)
    },
    
    onError: (error: any) => {
      logger.error('Registration failed', {
        component: 'useRegister',
        error: error.message
      })
      
      // Clear any stored auth data on registration failure
      clearAuthData()
    },
  })
}

/**
 * Logout mutation
 */
export const useLogout = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (): Promise<void> => {
      logger.debug('🚪 Starting logout process', { component: 'useLogout' })
      
      try {
        // Import store manager dynamically to avoid circular dependencies
        const { performLogoutCleanup } = await import('../stores/storeManager')
        
        logger.debug('🧹 Performing logout cleanup', { component: 'useLogout' })
        
        // Perform complete cleanup - stores and storage
        performLogoutCleanup()
        
        // Clear WebSocket connections and auth token
        const { websocketService } = await import('../services/websocket')
        websocketService.disconnectAll()
        websocketService.setAuthToken(null)
        
        logger.debug('🗑️ Clearing React Query cache', { component: 'useLogout' })
        
        // Clear React Query cache completely
        queryClient.clear()
        
        // Set user data to null immediately
        queryClient.setQueryData(authKeys.user(), null)
        
        logger.debug('✅ Logout completed with full cleanup', { component: 'useLogout' })
      } catch (error) {
        logger.error('❌ Error during logout cleanup', {
          component: 'useLogout',
          error: error instanceof Error ? error.message : 'Unknown error'
        })
        throw error
      }
    },
    
    onError: (error: any) => {
      logger.error('Logout failed', {
        component: 'useLogout',
        error: error.message
      })
      
      // Still perform cleanup even if something failed
      import('../stores/storeManager').then(({ performLogoutCleanup }) => {
        performLogoutCleanup()
        queryClient.clear()
        queryClient.setQueryData(authKeys.user(), null)
      })
    },
  })
}

/**
 * Main auth hook that combines all auth state
 */
export const useAuth = () => {
  const queryClient = useQueryClient()
  const { data: user, isLoading: isLoadingUser, error: userError, refetch } = useUser()
  const loginMutation = useLogin()
  const registerMutation = useRegister()
  const logoutMutation = useLogout()
  
  // Simplify the authentication check - rely on user data from React Query
  const isAuthenticated = !!user
  
  // Refresh user data function
  const refreshUser = async () => {
    try {
      logger.debug('Refreshing user data', { component: 'useAuth' })

      // Invalidate and refetch user data
      await queryClient.invalidateQueries({ queryKey: authKeys.user() })
      const result = await refetch()

      // Store user_type in localStorage after fetching user data
      // This is important for OAuth flows where user data is fetched after token is received
      if (result.data) {
        const userType = result.data.role || result.data.user_type || 'company_owner'
        localStorage.setItem('user_type', userType)

        logger.debug('User data refreshed successfully', {
          component: 'useAuth',
          userId: result.data?.id,
          userType: userType
        })
      }

      return result.data
    } catch (error) {
      logger.error('Failed to refresh user data', {
        component: 'useAuth',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }
  
  return {
    // State
    user,
    isAuthenticated,
    isLoading: isLoadingUser || loginMutation.isPending || registerMutation.isPending,
    error: userError || loginMutation.error || registerMutation.error,
    
    // Actions
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout: logoutMutation.mutateAsync, // Use mutateAsync for promise-based usage
    refreshUser, // Add refresh functionality
    
    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
  }
}